import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  ADMIN_ONLY_PERMISSIONS,
  isTenantScoped,
  type AuthMeResponse,
  type PermissionKey,
} from "@shared/types/auth";
import { readLoginFailure } from "./loginErrors";

interface AuthState {
  user: AuthMeResponse | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

/** Browser side. Polls `/api/auth/me` on mount, exposes login/logout. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthMeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setUser(data as AuthMeResponse | null);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    let res: Response;
    try {
      res = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
    } catch {
      // The request never reached anything - no server, DNS, offline.
      throw new Error("server_unreachable");
    }
    if (!res.ok) {
      /**
       * Only 401 means the credentials were wrong.
       *
       * Everything else is the server failing: a 502/504 from a dev proxy with
       * nothing behind it, a 500, a gateway timeout. Reporting those as a bad
       * password sends someone hunting for a problem that is not theirs - it
       * cost real time in this codebase more than once before the two cases
       * were separated.
       *
       * 429 is the THIRD case, and it is neither: the server is healthy and
       * saying "not so fast". Calling that unreachable makes the reader retry,
       * which is what keeps the window open. See `loginErrors`.
       */
      const failure = await readLoginFailure(res);
      throw Object.assign(new Error(failure.kind), {
        status: res.status,
        retryAfter: failure.retryAfter,
      });
    }
    setUser((await res.json()) as AuthMeResponse);
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
    setUser(null);
  }, []);

  // When the API wrapper detects a 401 mid-session, it dispatches this
  // event so we drop the cached user and the next RequireAuth render
  // sends them back to /login.
  useEffect(() => {
    const onInvalidated = () => setUser(null);
    window.addEventListener("foyer:unauthorized", onInvalidated);
    return () =>
      window.removeEventListener("foyer:unauthorized", onInvalidated);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Auth state, or null when rendered outside an <AuthProvider> (e.g. a
 *  component mounted in isolation in a test). Doesn't throw. */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthCtx);
}

/** True if the current user has the given permission (admins bypass). */
export function userCan(
  user: AuthMeResponse | null,
  key: PermissionKey
): boolean {
  if (!user) return false;
  if (user.type === "admin") return true;
  // Mirrors the server's `hasPermission`: a customer's user (company, granted
  // locations or content owner) never holds a staff-only key, whatever is
  // stored on it - so the UI must not offer what the API will refuse.
  if (ADMIN_ONLY_PERMISSIONS.has(key) && isTenantScoped(user)) return false;
  return (user.permissions ?? []).includes(key);
}

/** Hook variant. Convenient inside components. */
export function useCan(key: PermissionKey): boolean {
  const { user } = useAuth();
  return userCan(user, key);
}
